import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Modal, Form, Spinner, Alert } from 'react-bootstrap';
import { passService, TransferablePass } from '../services/services/passService';
import { useAuth } from '../contexts/AuthContext';
import { useConfirmation } from '../contexts/ConfirmationContext';
import './TransferTicketsPage.scss';

const TransferTicketsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { alert } = useConfirmation();
  
  const [loading, setLoading] = useState(true);
  const [passes, setPasses] = useState<TransferablePass[]>([]);
  const [showTransferForm, setShowTransferForm] = useState(false);
  const [selectedPass, setSelectedPass] = useState<TransferablePass | null>(null);
  const [transferring, setTransferring] = useState(false);
  const [formData, setFormData] = useState({
    new_name: '',
    new_email: '',
    new_phone: '',
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadTransferablePasses();
  }, [user]);

  const loadTransferablePasses = async () => {
    setLoading(true);
    try {
      const data = await passService.getTransferablePasses();
      setPasses(data.passes || []);
    } catch (error: any) {
      console.error('Error loading transferable passes:', error);
      alert({
        message: error.response?.data?.error || 'Failed to load transferable passes',
        variant: 'danger',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTransferClick = (pass: TransferablePass) => {
    setSelectedPass(pass);
    setFormData({
      new_name: '',
      new_email: '',
      new_phone: '',
    });
    setShowTransferForm(true);
  };

  const handleTransferSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPass) return;

    setTransferring(true);
    try {
      const result = await passService.createTransferRequest({
        pass_id: selectedPass.id,
        new_name: formData.new_name,
        new_email: formData.new_email,
        new_phone: formData.new_phone || undefined,
      });

      alert({
        message: result.message,
        variant: 'success',
      });

      setShowTransferForm(false);
      navigate(`/cart`);
    } catch (error: any) {
      console.error('Error creating transfer request:', error);
      alert({
        message: error.response?.data?.error || 'Failed to create transfer request',
        variant: 'danger',
      });
    } finally {
      setTransferring(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="transfer-tickets-page" style={{ marginTop: 120, padding: '2rem 0' }}>
      <Container>
        <div className="text-center mb-5">
          <div className="transfer-icon-wrapper mb-4">
            <div className="transfer-icon">
              <i className="fas fa-exchange-alt"></i>
            </div>
          </div>
          <h1 className="display-5 fw-bold mb-3">Transfer Your Tickets</h1>
          <p className="lead text-muted">Share your tickets with friends or family members</p>
        </div>

        {passes.length > 0 ? (
          <>
            <div className="stats-card mb-5">
              <Row className="align-items-center">
                <Col md={6}>
                  <h5 className="mb-0 fw-semibold">
                    <i className="fas fa-ticket-alt me-2 text-primary"></i>Available for Transfer
                  </h5>
                </Col>
                <Col md={6} className="text-md-end">
                  <span className="ticket-count-badge">
                    <i className="fas fa-check me-2"></i>{passes.length} Tickets
                  </span>
                </Col>
              </Row>
            </div>

            <Row>
              {passes.map((pass) => (
                <Col md={6} lg={4} key={pass.id} className="mb-4">
                  <Card className="ticket-card h-100">
                    <Card.Body>
                      {pass.event.banner_url && (
                        <div className="event-image mb-3">
                          <img src={pass.event.banner_url} alt={pass.event.title || 'Event'} />
                        </div>
                      )}
                      <h5 className="event-title">{pass.event.title}</h5>
                      <p className="event-category text-muted">{pass.category.name}</p>
                      
                      {pass.seat_info && (
                        <div className="seat-badge mb-3">
                          <i className="fas fa-chair me-2"></i>
                          <span>Seat {pass.seat_info.number}</span>
                        </div>
                      )}

                      <div className="ticket-details mb-3">
                        <div className="detail-item mb-2">
                          <small className="text-muted d-block">Current Attendee</small>
                          <strong>{pass.name}</strong>
                        </div>
                        <div className="detail-item mb-2">
                          <small className="text-muted d-block">Email</small>
                          <strong>{pass.email}</strong>
                        </div>
                        {pass.phone && (
                          <div className="detail-item mb-2">
                            <small className="text-muted d-block">Phone</small>
                            <strong>{pass.phone}</strong>
                          </div>
                        )}
                        <div className="detail-item">
                          <small className="text-muted d-block">Ticket ID</small>
                          <code className="ticket-id">#{pass.identifier}</code>
                        </div>
                      </div>

                      {pass.transfer_fee > 0 && (
                        <div className="fee-info mb-3">
                          <small className="text-muted d-block">Transfer Fee</small>
                          <strong className="text-danger">
                            {pass.currency_symbol}
                            {pass.transfer_fee.toFixed(2)}
                          </strong>
                        </div>
                      )}

                      <Button
                        variant="primary"
                        className="w-100"
                        onClick={() => handleTransferClick(pass)}
                      >
                        <i className="fas fa-exchange-alt me-2"></i>Transfer
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          </>
        ) : (
          <div className="empty-state text-center py-5">
            <div className="empty-icon mb-4">
              <i className="fas fa-ticket-alt"></i>
            </div>
            <h2>No Transferable Tickets</h2>
            <p className="text-muted">You don't have any tickets that can be transferred at the moment.</p>
            <Button variant="primary" onClick={() => navigate('/')}>
              <i className="fas fa-arrow-left me-2"></i>Back to Home
            </Button>
          </div>
        )}

        {/* Transfer Form Modal */}
        <Modal show={showTransferForm} onHide={() => setShowTransferForm(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              <i className="fas fa-user-plus me-2"></i>Transfer Ticket
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleTransferSubmit}>
            <Modal.Body>
              {selectedPass && (
                <>
                  <div className="ticket-preview mb-4">
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Event</Form.Label>
                          <Form.Control type="text" value={selectedPass.event.title || ''} readOnly />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Category</Form.Label>
                          <Form.Control type="text" value={selectedPass.category.name || ''} readOnly />
                        </Form.Group>
                      </Col>
                    </Row>
                  </div>

                  <div className="transfer-arrow text-center mb-4">
                    <i className="fas fa-arrow-down fa-2x text-primary"></i>
                  </div>
                </>
              )}

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Full Name *</Form.Label>
                    <Form.Control
                      type="text"
                      required
                      value={formData.new_name}
                      onChange={(e) => setFormData({ ...formData, new_name: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email Address *</Form.Label>
                    <Form.Control
                      type="email"
                      required
                      value={formData.new_email}
                      onChange={(e) => setFormData({ ...formData, new_email: e.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Phone Number (Optional)</Form.Label>
                <Form.Control
                  type="tel"
                  value={formData.new_phone}
                  onChange={(e) => setFormData({ ...formData, new_phone: e.target.value })}
                  placeholder="Optional"
                />
              </Form.Group>

              {selectedPass && selectedPass.transfer_fee > 0 && (
                <Alert variant="info">
                  <strong>Transfer Fee:</strong> {selectedPass.currency_symbol}
                  {selectedPass.transfer_fee.toFixed(2)}
                </Alert>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowTransferForm(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={transferring}>
                {transferring ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="fas fa-shopping-cart me-2"></i>Add to Cart
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </div>
  );
};

export default TransferTicketsPage;

